import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SidePanelProps, SidePanelTabConfig, SidePanelWidth } from '../../../types/DesignSystem/SidePanel';
import Button from '../Button/Button';
import Tab from '../Tab/Tab';
import Spinner from '../Spinner/Spinner';
import { useDismissable } from '../useDismissable';
import { usePresence } from '../usePresence';
import { MOTION, exitDurationFor } from '../motion';
import { useMergedRef } from '../useMergedRef';
import { withClassName } from '../cn';

/*
 * § 348 (2026-09-29) — `LEGACY_TABS` (Basic Info / Commend / Guide) lived here as the
 * default for a caller that passed no `tabs` (§ 53), so the Environment editor rendered
 * unchanged. It was a product's tab row inside the design system: every drawer that
 * forgot `tabs={[]}` grew it. The set moved to `EnvironmentSidePanel`, which passes it
 * explicitly and renders exactly as before.
 */
const NO_TABS: SidePanelTabConfig[] = [];

/**
 * md is the historical width; lg exists for dense edit panels (ADS: 360/480/600); xl
 * is the viewport-relative width a table panel needs — see `SidePanelWidth`.
 *
 * § 376 — the two fixed widths are capped at the viewport (`max-w-full`: a fixed element's
 * containing block is the viewport). Pinned `right-0`, a 416px drawer in a 320px viewport —
 * 1280px at 400% zoom — pushed its left edge off-screen, where nothing can scroll to it
 * (WCAG 1.4.10). Wherever the width fits, nothing moves.
 */
/*
 * § 394 — `xl` never narrower than `md`. 66% of the viewport is a table panel's width on a
 * desktop and the NARROWEST drawer on a phone-width screen: 211px at 320px, where its header
 * (title, Cancel, Save) measured 242px of content in 211px of panel. The floor is `md`'s 416px,
 * capped at the viewport like § 376's — so `xl` is the full width at 320px and 416px at 500px,
 * and nothing moves from 631px up, where 66% already clears 416.
 */
/*
 * § 398 — `2xl` (the DBM Rule panel) floors at 600px, so it takes § 376's cap like `md`/`lg`:
 * full width below a 600px viewport, unchanged from 600px up.
 */
const WIDTH_CLASS: Record<SidePanelWidth, string> = {
  md: 'tw-w-[416px] tw-max-w-full',
  lg: 'tw-w-[600px] tw-max-w-full',
  xl: 'tw-w-[66%] tw-min-w-[min(416px,100%)]',
  '2xl': 'tw-w-[clamp(600px,70vw,920px)] tw-max-w-full',
};

/*
 * GENERIC over the tab-id union (§ 53): consumers pass `onTabChange={setActiveTab}`
 * typed to their own union, and widening the prop to `string` would be a
 * contravariance break in every one of them. `TabId` infers from whichever of
 * `tabs`/`activeTab`/`onTabChange` the caller provides.
 */
const SidePanelInner = <TabId extends string = string>({
  open: openProp,
  isOpen: isOpenProp,
  onClose,
  onSave,
  onSaveError,
  title,
  tabs,
  activeTab,
  onTabChange,
  headerActions,
  subheader,
  footer,
  width = 'md',
  loading: loadingProp,
  isLoading: isLoadingProp,
  closeOnOutsideClick = true,
  ariaLabel,
  'aria-label': ariaLabelNative,
  children,
  'data-testid': dataTestId,
  className = '',
  ...rest
}: SidePanelProps<TabId>, forwardedRef: React.ForwardedRef<HTMLDivElement>) => {
  const panelRef = useRef<HTMLDivElement>(null);
  /* § 384 — the caller's ref reaches the panel too; `panelRef` stays the focus surface. */
  const setPanelRef = useMergedRef<HTMLDivElement>(panelRef, forwardedRef);
  /* § 410 — part of the layer: its click closes the drawer, so it stays live while the page
     behind is inert. */
  const backdropRef = useRef<HTMLDivElement>(null);
  // § 331 — `open` is the canonical name; `isOpen` is the deprecated alias.
  const isOpen = openProp ?? isOpenProp ?? false;
  const isLoading = loadingProp ?? isLoadingProp ?? false;
  const { isPresent, phase } = usePresence(isOpen, exitDurationFor('panel'));

  /*
    § 302 — the tab ↔ content association. The drawer composes `Tab` for the row and
    renders the content itself, so `Tab` alone could not wire the APG pair. The drawer
    gives each tab a stable DOM id, tells `Tab` which element is the panel (`panelId`),
    and marks its content region as that panel, named by the selected tab. All of it
    only while a tab row renders; a bare drawer keeps a plain content region.
  */
  const uid = React.useId();
  const tabDomId = (id: string) => `${uid}-tab-${id.replace(/\s+/g, '-')}`;
  const contentId = `${uid}-content`;
  /* § 348 — what a NODE title names the dialog through, when no `aria-label` does. */
  const titleId = `${uid}-title`;
  /* § 354 — the native spelling wins over the deprecated `ariaLabel`. */
  const dialogName = ariaLabelNative ?? ariaLabel;

  /*
    Scroll-aware chrome (§ 222) — Dialog's § 53 signature, applied to the drawer:
    the header band's shadow appears only once content has actually scrolled UNDER
    it, so a short panel stays flat and a long one answers "is there more?" the
    moment it moves. Same recipe as Dialog: measured on scroll, re-measured by a
    ResizeObserver because async rows can grow the content without a scroll event
    (jsdom has no ResizeObserver, hence the guard).
  */
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrolledUnder, setScrolledUnder] = useState(false);
  const measureScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setScrolledUnder(el.scrollTop > 0);
  }, []);
  React.useLayoutEffect(() => {
    if (!isPresent) return undefined;
    measureScroll();
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measureScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPresent, measureScroll]);

  /*
    § 402 — `loading` before the content has shown is the FIRST LOAD; after it, the content is
    BUSY. One flag carried both, and both rendered the full-panel spinner, so a save swapped the
    form out for "Loading panel content" until the request came back (Environment create and
    update: ChannelTable → EnvironmentSidePanel). Table makes the same split (RefreshOverlay):
    a first load has nothing to show, a refresh keeps what is on screen. So once this open has
    rendered its content, a later `loading` keeps it mounted, `inert` (no edit lands mid-save)
    and `aria-busy`. Reset when the panel has fully closed, so the next open starts over.
  */
  const [contentShown, setContentShown] = useState(false);
  React.useEffect(() => {
    if (!isPresent) setContentShown(false);
    else if (!isLoading) setContentShown(true);
  }, [isPresent, isLoading]);
  const firstLoad = isLoading && !contentShown;
  const busy = isLoading && contentShown;

  useDismissable({
    // `isPresent`, not `isOpen`: usePresence defers the mount by one render, so an
    // effect keyed on `isOpen` runs while the panel is still null — it then finds no
    // focusable surface and never re-runs, and focus never enters the dialog. Keying
    // on `isPresent` means the effect fires on the render that actually mounts it,
    // and the focus restore in its cleanup lands after the exit animation.
    isOpen: isPresent,
    onDismiss: onClose,
    modal: true,
    surface: panelRef,
    backdrop: backdropRef,
    // …but stop listening for Escape the moment it starts closing, so a second
    // press during the exit does not call onClose again.
    closeOnEscape: isOpen,
    // Unlike Dialog, this backdrop IS reachable: the panel is a 416px strip on
    // the right, not a full-screen container, so the backdrop receives clicks
    // beside it and its own onClick already closes the panel.
    closeOnOutsidePointerDown: false,
  });

  // Stay mounted through the exit animation, then unmount.
  if (!isPresent) {
    return null;
  }

  const exiting = phase === 'exiting';

  /* § 348 — no `tabs`, no tab row (it used to mean the Environment editor's three). The
     cast is safe: an empty list satisfies any `TabId`. */
  const effectiveTabs = (tabs ?? NO_TABS) as SidePanelTabConfig<TabId>[];
  const hasTabRow = effectiveTabs.length > 0;
  /* Mirrors Tab's own fallback (`activeTabId || tabs[0]?.id`), so the panel is named by
     the tab that is actually selected when the caller passes none. */
  const selectedTabId = activeTab ?? effectiveTabs[0]?.id;

  const handleSave = async () => {
    if (onSave) {
      try {
        await onSave(null);
      } catch (error) {
        /* § 348 — the caller's to show, when it asks; otherwise logged, as before. */
        if (onSaveError) onSaveError(error);
        else console.error('Error saving:', error);
      }
    }
  };

  /*
   * Portal to <body>, exactly like Dialog and for the same reason (§ 93): the
   * caller's DOM context must never decide whether the panel is visible. The
   * Guide widget panel (§ 91) mounted this inline inside the app shell's
   * <main> — a `tw-z-10` flex row under a `tw-z-50` header row — and because a
   * fixed element stacks inside its nearest z-indexed ancestor's context, the
   * panel's whole header band (title, actions, the close X) painted UNDER the
   * app header. Under <body> the panel resolves in the root stacking context,
   * where its z-50 + later-DOM-order beats the header — the mechanism Dialog
   * has always relied on. The scrim is z-50 (not the old z-40) for the same
   * reason: at 40 it left the app header fully lit over an aria-modal surface.
   */
  return createPortal(
    <>
      {/* Backdrop */}
      <div data-testid={dataTestId ? `${dataTestId}-backdrop` : 'sidepanel-backdrop'}
        ref={backdropRef}
        className={`tw-fixed tw-inset-0 ${MOTION.overlay.scrim} tw-z-overlay ${
          exiting ? MOTION.overlay.exit : MOTION.overlay.enter
        }`}
        /* § 348 — the scrim closes the drawer unless the caller says it must not: a
           drawer of unsaved edits should not lose them to a stray click beside it. */
        onClick={closeOnOutsideClick ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Side Panel */}
      <div
        /* § 384 — rest first (the Button.tsx rule); the dialog's own wiring below wins. */
        {...rest}
        ref={setPanelRef}
        /* Full-height (§ 53). It was `top-16` — 64px, under a 60px app header:
           a 4px scrim-lit gap, on a surface that is aria-modal and BLOCKS the
           header it did not cover. ADS's Drawer is full-height; so is this. */
        className={withClassName(`tw-fixed tw-right-0 tw-top-0 tw-bottom-0 ${WIDTH_CLASS[width]} tw-bg-surface-raised tw-shadow-ds-lg tw-z-overlay tw-flex tw-flex-col tw-font-noto-sans ${
          exiting ? MOTION.panel.exit : MOTION.panel.enter
        }`, className)}
        role="dialog"
        aria-modal="true"
        aria-label={dialogName ?? (typeof title === 'string' ? title : undefined)}
        /* § 348 — a NODE title names the dialog by its rendered text when no `aria-label`
           does; it used to leave the dialog unnamed (the reason § 94 exists). */
        aria-labelledby={dialogName === undefined && typeof title !== 'string' ? titleId : undefined}
        aria-busy={isLoading || undefined}
        data-testid={dataTestId}
      >
        {/* The header BAND — title row plus the tab row when there is one — carries
            the § 222 scrolled-under shadow as one unit, because whichever of the two
            is lowest is the edge content slides beneath. `relative` so the shadow
            paints above whatever ground that content brings (Dialog's § 53 note). */}
        <div
          className={`tw-relative tw-transition-shadow motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${scrolledUnder ? 'tw-shadow-ds-sm' : ''}`}
          data-testid={dataTestId ? `${dataTestId}-band` : undefined}
        >
        {/* Header. The bottom rule closes the title band (§ 80 — same argument as
            Dialog's § 35: without it the title floats over the content padding).
            When tabs render, the Tab row's own baseline IS that rule, so adding
            one here would stack two rules 12px apart. */}
        {/* § 296 — `px-6`, the content's inset. The header sat at 16 while the content
            sat at 24 and the first tab label at 12, so the drawer had THREE left edges
            (measured live: +16, +12, +24 from the panel edge). Dialog aligns its header,
            content and footer at 24; the drawer now does the same. */}
        {/* § 394 — below 480px the drawer is the whole screen, and it takes Dialog's narrow
            inset: 16px on the header, the tab row and the content together, so the three
            left edges § 296 aligned stay one edge. */}
        <div
          className={`tw-px-6 narrow:tw-px-4 tw-py-3 ${effectiveTabs.length > 0 ? '' : 'tw-border-b tw-border-edge'}`}
          data-testid={dataTestId ? `${dataTestId}-header` : undefined}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            {/* § 275 — a drawer owns the screen the way a Dialog does, and the two must
                agree: `heading-3` (16/600), up from the 14/600 of a column header. */}
            <div id={titleId} className="tw-flex-grow tw-text-ink tw-text-heading-3">
              {/* The split-on-colon emphasis is the Environment editor's "Edit: <name>"
                  shape. It used to apply unconditionally, appending ":" to EVERY string
                  title — `title="Filter"` rendered "Filter:" (§ 67). Plain titles now
                  render whole.
                  § 402 — split at the FIRST colon only. `split(':')[1]` kept the text between
                  the first and second colons and dropped the rest, so an environment named
                  "Prod: EU" rendered "Edit Environment: Prod". */}
              {title && typeof title === 'string' ? (
                title.includes(':') ? (
                  <>
                    <span className="tw-font-semibold">{title.slice(0, title.indexOf(':'))}:</span>
                    {title.slice(title.indexOf(':') + 1) && (
                      <span className="tw-font-normal">{title.slice(title.indexOf(':') + 1)}</span>
                    )}
                  </>
                ) : (
                  <span className="tw-font-semibold">{title}</span>
                )
              ) : (
                title
              )}
            </div>
            <div className="tw-flex tw-gap-2 tw-shrink-0">
              {/* The Cancel/Save pair is the LEGACY default (§ 53), not the
                  contract: `headerActions` replaces it wholesale. What the
                  buttons do is the caller's business — a drawer is a surface. */}
              {headerActions ?? (
                <>
                  {/* § 335 — `focusableWhenDisabled`: the pair disables itself while the
                      save runs, and the Save the user just pressed is the button holding
                      focus. Natively disabled, focus fell to <body>; now it stays put,
                      with the same disabled look and the click still blocked. */}
                  <Button
                    variant="tertiary"
                    onClick={onClose}
                    disabled={isLoading}
                    focusableWhenDisabled
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isLoading}
                    focusableWhenDisabled
                  >
                    {/* § 402 — "Saving..." only once there is content to save; during the
                        first load nothing is being saved, so the disabled button still says Save. */}
                    {busy ? 'Saving...' : 'Save'}
                  </Button>
                </>
              )}
            </div>
          </div>
          {/* § 326 — under the title, inside the band: outside the scroll container, so
              it cannot scroll away and nothing can scroll behind it. (A `sticky` row in
              the content cannot do this — Chromium pins it at the container's PADDING
              edge, leaving the content's 16px inset open above it for entries to show
              through.) */}
          {subheader && (
            <div
              className="tw-mt-1"
              data-testid={dataTestId ? `${dataTestId}-subheader` : undefined}
            >
              {subheader}
            </div>
          )}
        </div>

        {/* Tabs — none at all when the caller passes `tabs={[]}`. */}
        {hasTabRow && (
          <Tab
            tabs={effectiveTabs.map((tab) => ({
              ...tab,
              /* § 302 — the drawer names the tab so its content region can point back at it. */
              htmlId: tabDomId(tab.id),
              'data-testid':
                tab['data-testid'] ?? (dataTestId ? `${dataTestId}-tab-${tab.id}` : undefined),
            }))}
            activeTabId={activeTab}
            onChange={(tabId) => onTabChange?.(tabId as TabId)}
            panelId={contentId}
            className="tw-mt-3"
            /* § 296 — the first tab label lands on the header's and content's 24:
               the tab's own `px-3` is 12, the list adds the other 12. On the ROW, so
               the rule under the tabs stays full-bleed (see `TabProps.listClassName`).
               § 394 — narrow, the edge is 16: the list adds 4. */
            listClassName="tw-pl-3 narrow:tw-pl-1"
          />
        )}
        </div>

        {/* Content */}
        {/*
          Was `<CompatioLoader />` — a 300x300px lottie animation, sized for a page,
          rendered inside a 420px panel, with no accessible label. The panel now
          keeps its own scroll container and centres a spinner in it, so the panel
          does not change shape while it loads.
        */}
        <div
          ref={contentRef}
          onScroll={measureScroll}
          /* § 302 — the drawer's content IS the tab panel while a tab row renders:
             named by the selected tab, pointed at by its `aria-controls`. */
          id={contentId}
          role={hasTabRow ? 'tabpanel' : undefined}
          aria-labelledby={hasTabRow && selectedTabId !== undefined ? tabDomId(selectedTabId) : undefined}
          /* Themed default ink for slot content — § 55. `overscroll-contain`
             (§ 222): the wheel stops at the panel's own end rather than chaining
             to the page behind the scrim. */
          className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 narrow:tw-px-4 tw-py-4 tw-text-ink"
          data-testid={dataTestId ? `${dataTestId}-content` : undefined}
          /* § 402 — busy (a save, a refetch): the content stays, inert and marked busy. */
          inert={busy || undefined}
          aria-busy={busy || undefined}
        >
          {firstLoad
            ? (
              <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-py-10">
                <Spinner
                  size="lg"
                  label="Loading panel content"
                  data-testid={dataTestId ? `${dataTestId}-loading` : 'sidepanel-loading'}
                />
              </div>
            )
            : children}
        </div>

        {/* Footer — outside the scroll container, so it never scrolls away. Same
            inset as the header and content (§ 296 / § 394). */}
        {footer && (
          <div
            className="tw-shrink-0 tw-border-t tw-border-edge tw-px-6 narrow:tw-px-4 tw-py-3"
            data-testid={dataTestId ? `${dataTestId}-footer` : undefined}
          >
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

/* § 384 — `forwardRef` drops a generic component's type parameter; the cast restores `TabId`. */
const SidePanel = React.forwardRef(SidePanelInner) as <TabId extends string = string>(
  props: SidePanelProps<TabId> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

export default SidePanel;
